import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { DeleteOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { financeService } from "../../services/financeService";
import { adminRestaurantPosService } from "../../services/adminService";
import { formatLkr } from "../../utils/currency";
import { hasPermission } from "../../utils/permissions";
import {
  convertQuantity,
  resolveUnit,
  roundQuantity,
  stockUnitGroups,
  unitOptions,
} from "../../utils/units";
import StockCategoryModal from "./StockCategoryModal";
import type {
  FinanceDepartment,
  RecipeLine,
  StockCategory,
  StockItem,
  StockMovement,
  StockUnit,
  StockValuationReport,
} from "../../types";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const MOVEMENT_TYPES = [
  { value: "receipt", label: "Received" },
  { value: "issue", label: "Issued" },
  { value: "waste", label: "Write-off" },
  { value: "adjustment", label: "Count adjustment" },
];

const MOVEMENT_COLOR: Record<string, string> = {
  receipt: "green",
  issue: "blue",
  waste: "volcano",
  adjustment: "purple",
};

interface QtyLine {
  key: string;
  stock_item_id?: string;
  quantity: number;
  counted_quantity?: number;
}

interface RecipeDraftLine {
  key: string;
  stock_item_id?: string;
  quantity: number;
  /** Unit the author is entering; defaults to the stock item's unit. */
  unit?: string;
  /** Price of one stock unit; null means use the item's average cost. */
  manual_unit_price: number | null;
}

const blankQtyLine = (): QtyLine => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  quantity: 1,
  counted_quantity: 0,
});

const blankRecipeLine = (): RecipeDraftLine => ({
  key: `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  quantity: 1,
  manual_unit_price: null,
});

/**
 * Every recipe cell is top-aligned and every editable control reserves the
 * same height. The price cell always has helper text below it; without the
 * reserved row it would pull its input out of line with the other fields.
 */
const recipeFieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minHeight: 50,
};
const recipeTextStyle: React.CSSProperties = {
  minHeight: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
};
const topAlignCell = () => ({ style: { verticalAlign: "top" as const } });

/**
 * The cupboard.
 *
 * Everything here is quantity and cost, but each screen carries the control
 * that says whether the shelf and the ledger still agree: the valuation
 * shows its variance against GL 1200 rather than a tick, and coverage of
 * recipes is reported as coverage so an unconfigured item reads as a gap in
 * the Food Cost % rather than as a bargain.
 */
const InventoryPage: React.FC = () => {
  const canEdit = hasPermission("inventory:edit");
  const canView = hasPermission("inventory:view");

  const [items, setItems] = useState<StockItem[]>([]);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [departments, setDepartments] = useState<FinanceDepartment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ---- stock master ---- */
  const [itemModal, setItemModal] = useState<{ open: boolean; editing?: StockItem }>({ open: false });
  const [itemForm] = Form.useForm();
  const [categories, setCategories] = useState<StockCategory[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [units, setUnits] = useState<StockUnit[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const itemUnit = Form.useWatch("unit", itemForm);
  const selectedCategory = Form.useWatch("category", itemForm);

  /* ---- movements ---- */
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [movItem, setMovItem] = useState<string | undefined>();
  const [movType, setMovType] = useState<string | undefined>();
  const [movRange, setMovRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [movLoading, setMovLoading] = useState(false);

  /* ---- recipes ---- */
  const [recipes, setRecipes] = useState<RecipeLine[]>([]);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [recipeModal, setRecipeModal] = useState<{
    open: boolean;
    menuItemId?: string;
    menuItemName?: string;
  }>({ open: false });
  const [recipeLines, setRecipeLines] = useState<RecipeDraftLine[]>([blankRecipeLine()]);
  const [recipeServings, setRecipeServings] = useState(1);
  const [recipeSaving, setRecipeSaving] = useState(false);

  /* ---- write-off & count ---- */
  const [wasteForm] = Form.useForm();
  const [countForm] = Form.useForm();
  const [wasteLines, setWasteLines] = useState<QtyLine[]>([blankQtyLine()]);
  const [countLines, setCountLines] = useState<QtyLine[]>([blankQtyLine()]);
  const [posting, setPosting] = useState(false);

  /* ---- valuation ---- */
  const [valuationDate, setValuationDate] = useState<Dayjs | null>(dayjs());
  const [valuation, setValuation] = useState<StockValuationReport | null>(null);
  const [valLoading, setValLoading] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await financeService.getStockItems(includeInactive));
    } catch (e: any) {
      setError(e?.message || "Failed to load stock items");
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  const loadCategories = useCallback(async () => {
    setCategoryLoading(true);
    try {
      setCategories(await financeService.getStockCategories(true));
    } catch (e: any) {
      message.error(e?.message || "Failed to load stock categories");
    } finally {
      setCategoryLoading(false);
    }
  }, []);

  const loadUnits = useCallback(async () => {
    setUnitsLoading(true);
    try {
      setUnits(await financeService.getStockUnits());
    } catch (e: any) {
      message.error(e?.message || "Failed to load stock units");
      setUnits([]);
    } finally {
      setUnitsLoading(false);
    }
  }, []);

  const loadMovements = useCallback(async () => {
    setMovLoading(true);
    try {
      setMovements(
        await financeService.getStockMovements({
          stock_item_id: movItem,
          movement_type: movType,
          from: movRange ? movRange[0].format("YYYY-MM-DD") : undefined,
          to: movRange ? movRange[1].format("YYYY-MM-DD") : undefined,
          limit: 200,
        }),
      );
    } catch (e: any) {
      message.error(e?.message || "Failed to load movements");
    } finally {
      setMovLoading(false);
    }
  }, [movItem, movType, movRange]);

  const loadRecipes = useCallback(async () => {
    try {
      setRecipes(await financeService.getRecipes());
    } catch (e: any) {
      message.error(e?.message || "Failed to load recipes");
    }
  }, []);

  const loadValuation = useCallback(async () => {
    setValLoading(true);
    try {
      setValuation(
        await financeService.getStockValuation(valuationDate ? valuationDate.format("YYYY-MM-DD") : undefined),
      );
    } catch (e: any) {
      message.error(e?.message || "Failed to build the valuation");
    } finally {
      setValLoading(false);
    }
  }, [valuationDate]);

  useEffect(() => {
    if (!canView) return;
    loadItems();
    loadCategories();
    loadUnits();
    loadRecipes();
    financeService
      .getDepartments()
      .then(setDepartments)
      .catch(() => setDepartments([]));
    // The menu is restaurant data: an operator holding inventory:view but not
    // restaurant:view still sees every recipe that exists, and simply cannot
    // start a new one for an item they are not allowed to list.
    adminRestaurantPosService
      .getMenuItems()
      .then((res: any) => setMenuItems(res?.data || []))
      .catch(() => setMenuItems([]));
  }, [canView, loadCategories, loadItems, loadRecipes, loadUnits]);

  useEffect(() => {
    if (canView) loadValuation();
  }, [canView, loadValuation]);

  useEffect(() => {
    if (canView) loadMovements();
  }, [canView, loadMovements]);

  /* ------------------------------------------------------------
   * Stock master
   * ------------------------------------------------------------ */

  const refreshItemData = useCallback(
    () => Promise.all([loadItems(), loadCategories(), loadValuation()]),
    [loadCategories, loadItems, loadValuation],
  );

  const activeCategories = useMemo(
    () => categories.filter((category) => category.is_active),
    [categories],
  );

  /**
   * Recipe editing must still understand an ingredient that the stock tab is
   * filtering out. Active-only hides inactive items from the master table, but
   * an existing recipe line may legitimately point at one; the recipe endpoint
   * carries enough of the item to keep that line editable.
   */
  const recipeItemById = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        code: string;
        name: string;
        unit: string;
        avg_cost: number;
        is_active: boolean;
      }
    >();
    for (const item of items) {
      map.set(item.id, {
        id: item.id,
        code: item.code,
        name: item.name,
        unit: item.unit,
        avg_cost: Number(item.avg_cost ?? 0),
        is_active: item.is_active,
      });
    }
    for (const line of recipes) {
      if (!map.has(line.stock_item_id)) {
        map.set(line.stock_item_id, {
          id: line.stock_item_id,
          code: line.stock_item_code,
          name: line.stock_item_name,
          unit: line.unit,
          avg_cost: Number(line.avg_cost ?? 0),
          is_active: line.stock_item_is_active,
        });
      }
    }
    return map;
  }, [items, recipes]);

  const recipeStockQuantity = (line: RecipeDraftLine): number | null => {
    const item = line.stock_item_id ? recipeItemById.get(line.stock_item_id) : undefined;
    if (!item) return null;
    const converted = convertQuantity(
      Number(line.quantity),
      line.unit || item.unit,
      item.unit,
      units,
    );
    return converted === null ? null : roundQuantity(converted);
  };

  const recipeUnitPrice = (line: RecipeDraftLine): number => {
    if (line.manual_unit_price !== null && line.manual_unit_price !== undefined) {
      return Number(line.manual_unit_price);
    }
    const item = line.stock_item_id ? recipeItemById.get(line.stock_item_id) : undefined;
    return Number(item?.avg_cost ?? 0);
  };

  const openItemModal = (editing?: StockItem) => {
    setItemModal({ open: true, editing });
    itemForm.resetFields();
    itemForm.setFieldsValue(
      editing
        ? {
            code: editing.code,
            name: editing.name,
            // Show the canonical code for a known legacy alias (for example
            // `litres` becomes `l`), but keep a genuinely unknown legacy unit.
            unit: resolveUnit(editing.unit, units)?.code ?? editing.unit,
            category: editing.category,
            reorder_level: editing.reorder_level,
            is_active: editing.is_active,
          }
        : {
            unit: "pcs",
            category: activeCategories[0]?.code,
            reorder_level: 0,
            is_active: true,
          },
    );
  };

  const categoryOptions = useMemo(() => {
    const options = activeCategories.map((category) => ({
      value: category.code,
      label: category.name,
    }));
    const current = categories.find((category) => category.code === selectedCategory);
    if (current && !current.is_active) {
      options.push({ value: current.code, label: `${current.name} (inactive)` });
    }
    return options;
  }, [activeCategories, categories, selectedCategory]);

  const itemUnitOptions = useMemo(() => {
    const groups = stockUnitGroups(units);
    if (!itemUnit) return groups;

    const hasCurrentOption = groups.some((group) =>
      group.options.some((option) => option.value === itemUnit),
    );
    if (!hasCurrentOption) {
      const resolved = resolveUnit(itemUnit, units);
      groups.push({
        label: "Current value",
        options: [
          {
            value: itemUnit,
            label:
              units.length > 0 && !resolved?.code
                ? `${itemUnit} (legacy)`
                : itemUnit,
          },
        ],
      });
    }
    return groups;
  }, [itemUnit, units]);

  const editingUnitKey = itemModal.editing
    ? resolveUnit(itemModal.editing.unit, units)?.key
    : undefined;
  const selectedUnitKey = itemUnit ? resolveUnit(itemUnit, units)?.key : undefined;
  const itemUnitChanged = Boolean(
    itemModal.editing &&
      itemUnit &&
      !itemModal.editing.unit_locked &&
      editingUnitKey !== selectedUnitKey,
  );

  const submitItem = async () => {
    const values = await itemForm.validateFields();
    try {
      if (itemModal.editing) {
        await financeService.updateStockItem(itemModal.editing.id, values);
        message.success("Stock item updated");
      } else {
        await financeService.createStockItem(values);
        message.success("Stock item created");
      }
      setItemModal({ open: false });
      await refreshItemData();
    } catch (e: any) {
      message.error(e?.message || "Failed to save stock item");
    }
  };

  const removeItem = async (row: StockItem) => {
    try {
      const result = await financeService.deleteStockItem(row.id);
      if (result.deactivated) {
        message.warning(`${row.code} has history or a recipe, so it was deactivated instead of deleted`);
      } else {
        message.success(`${row.code} removed`);
      }
      await refreshItemData();
    } catch (e: any) {
      message.error(e?.message || "Failed to remove stock item");
    }
  };

  const itemColumns = [
    { title: "Code", dataIndex: "code", key: "code", width: 100 },
    { title: "Item", dataIndex: "name", key: "name", ellipsis: true },
    { title: "Unit", dataIndex: "unit", key: "unit", width: 70 },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category_name",
      width: 120,
      render: (v: string | null, row: StockItem) => <Tag>{v || row.category}</Tag>,
    },
    {
      title: "Reorder at",
      dataIndex: "reorder_level",
      key: "reorder_level",
      width: 120,
      align: "right" as const,
      render: (v: number, row: StockItem) => `${Number(v)} ${row.unit}`,
    },
    {
      title: "On hand",
      dataIndex: "on_hand",
      key: "on_hand",
      width: 130,
      align: "right" as const,
      render: (v: number, row: StockItem) => {
        const onHand = Number(v);
        const reorder = Number(row.reorder_level);
        const low = row.is_active && reorder > 0 && onHand <= reorder;
        const out = row.is_active && onHand <= 0;
        return (
          <Space size={4}>
            {low && <Tag color="warning">Low</Tag>}
            {!low && out && <Tag>Out</Tag>}
            <Text type={onHand < 0 ? "danger" : undefined}>
              {onHand} {row.unit}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Unit cost",
      dataIndex: "avg_cost",
      key: "avg_cost",
      width: 130,
      align: "right" as const,
      render: (v: number) => formatLkr(v),
    },
    {
      title: "Value",
      dataIndex: "stock_value",
      key: "stock_value",
      width: 140,
      align: "right" as const,
      render: (v: number) => <Text strong>{formatLkr(v)}</Text>,
    },
    {
      title: "State",
      dataIndex: "is_active",
      key: "is_active",
      width: 90,
      render: (v: boolean) =>
        v ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag>,
    },
    {
      title: "",
      key: "actions",
      width: 200,
      render: (_: any, row: StockItem) =>
        canEdit ? (
          <Space size={4}>
            <Button size="small" onClick={() => openItemModal(row)}>
              Edit
            </Button>
            <Button
              size="small"
              onClick={() =>
                financeService
                  .updateStockItem(row.id, { is_active: !row.is_active })
                  .then(async () => {
                    message.success(row.is_active ? "Deactivated" : "Reactivated");
                    await refreshItemData();
                  })
                  .catch((e: any) => message.error(e?.message || "Failed"))
              }
            >
              {row.is_active ? "Deactivate" : "Reactivate"}
            </Button>
            <Popconfirm
              title="Delete this stock item?"
              description="Unused items are deleted. An item with purchase, movement or recipe history is deactivated instead; history is never removed."
              onConfirm={() => removeItem(row)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} title="Delete" />
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  /* ------------------------------------------------------------
   * Recipes
   * ------------------------------------------------------------ */

  const grouped = useMemo(() => {
    const map = new Map<string, { name: string; lines: RecipeLine[] }>();
    for (const r of recipes) {
      const entry = map.get(r.menu_item_id) || { name: r.menu_item_name, lines: [] };
      entry.lines.push(r);
      map.set(r.menu_item_id, entry);
    }
    return Array.from(map.entries()).map(([menuItemId, v]) => ({
      menuItemId,
      name: v.name,
      lines: v.lines,
      costPerServing: v.lines.reduce((total, line) => {
        const price =
          line.manual_unit_price === null || line.manual_unit_price === undefined
            ? Number(line.avg_cost || 0)
            : Number(line.manual_unit_price);
        return total + Number(line.quantity) * price;
      }, 0),
    }));
  }, [recipes]);

  const openRecipe = (entry?: { menuItemId: string; name: string; lines: RecipeLine[] }) => {
    setRecipeModal({
      open: true,
      menuItemId: entry?.menuItemId,
      menuItemName: entry?.name,
    });
    setRecipeServings(1);
    setRecipeLines(
      entry?.lines.map((l) => ({
        key: `r-${l.id}`,
        stock_item_id: l.stock_item_id,
        quantity: Number(l.quantity),
        unit: l.unit,
        manual_unit_price:
          l.manual_unit_price === null || l.manual_unit_price === undefined
            ? null
            : Number(l.manual_unit_price),
      })) || [blankRecipeLine()],
    );
  };

  const saveRecipe = async () => {
    const menuItemId = recipeModal.menuItemId;
    if (!menuItemId) {
      message.error("Choose the menu item this recipe belongs to");
      return;
    }
    if (units.length === 0) {
      message.error("The stock unit list is still loading — try again in a moment");
      return;
    }
    if (recipeLines.some((l) => !l.stock_item_id || !(Number(l.quantity) > 0))) {
      message.error("Every recipe line needs an item and a quantity above zero");
      return;
    }
    for (const line of recipeLines) {
      const item = recipeItemById.get(line.stock_item_id!);
      if (!item) {
        message.error("Choose a stock item for every recipe line");
        return;
      }
      const converted = convertQuantity(
        Number(line.quantity),
        line.unit || item.unit,
        item.unit,
        units,
      );
      if (converted === null) {
        message.error(`${line.unit || item.unit} cannot be converted to ${item.unit}`);
        return;
      }
      if (roundQuantity(converted) <= 0) {
        message.error(`Quantity is too small for ${item.unit}`);
        return;
      }
    }
    setRecipeSaving(true);
    try {
      await financeService.saveRecipe(
        menuItemId,
        recipeLines.map((l) => ({
          stock_item_id: l.stock_item_id!,
          quantity: Number(l.quantity),
          unit: l.unit,
          manual_unit_price: l.manual_unit_price,
        })),
      );
      message.success("Recipe saved");
      setRecipeModal({ open: false });
      await loadRecipes();
    } catch (e: any) {
      message.error(e?.message || "Failed to save recipe");
    } finally {
      setRecipeSaving(false);
    }
  };

  /* ------------------------------------------------------------
   * Write-offs and counts — both refuse a closed business date
   * ------------------------------------------------------------ */

  const qtyColumns = (
    field: "quantity" | "counted_quantity",
    showBook = false,
  ) => [
    {
      title: "Stock item",
      key: "item",
      render: (_: any, l: QtyLine) => (
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: "100%" }}
          placeholder="Item"
          value={l.stock_item_id}
          options={items
            .filter((i) => i.is_active)
            .filter((i) => field !== "quantity" || Number(i.on_hand) > 0)
            .map((i) => ({
              value: i.id,
              label:
                field === "quantity"
                  ? `${i.code} — ${i.name} (${Number(i.on_hand)} ${i.unit})`
                  : `${i.code} — ${i.name}`,
            }))}
          onChange={(v) =>
            field === "quantity"
              ? setWasteLines((prev) => prev.map((x) => (x.key === l.key ? { ...x, stock_item_id: v } : x)))
              : setCountLines((prev) => prev.map((x) => (x.key === l.key ? { ...x, stock_item_id: v } : x)))
          }
        />
      ),
    },
    ...(showBook
      ? [
          {
            title: "Book",
            key: "book",
            width: 110,
            align: "right" as const,
            render: (_: any, l: QtyLine) => {
              const item = items.find((i) => i.id === l.stock_item_id);
              return item ? item.on_hand : "—";
            },
          },
        ]
      : []),
    {
      title: field === "quantity" ? "Quantity" : "Counted",
      key: "value",
      width: 150,
      render: (_: any, l: QtyLine) => (
        <InputNumber
          min={field === "quantity" ? 0.01 : 0}
          step={1}
          style={{ width: "100%" }}
          value={l[field]}
          onChange={(v) => {
            const patch = { [field]: Number(v ?? 0) };
            const setter = field === "quantity" ? setWasteLines : setCountLines;
            setter((prev) => prev.map((x) => (x.key === l.key ? { ...x, ...patch } : x)));
          }}
        />
      ),
    },
  ];

  const addQtyLine = (setter: React.Dispatch<React.SetStateAction<QtyLine[]>>) =>
    setter((prev) => [...prev, blankQtyLine()]);

  const postWaste = async () => {
    const values = await wasteForm.validateFields();
    if (wasteLines.some((l) => !l.stock_item_id || !(Number(l.quantity) > 0))) {
      message.error("Every write-off line needs an item and a quantity above zero");
      return;
    }
    setPosting(true);
    try {
      const res = await financeService.recordWaste({
        business_date: values.business_date.format("YYYY-MM-DD"),
        reason: values.reason,
        department_id: values.department_id || null,
        lines: wasteLines.map((l) => ({
          stock_item_id: l.stock_item_id,
          quantity: Number(l.quantity),
        })),
      });
      message.success(`Write-off recorded — ${formatLkr(res?.value || 0)} issued out`);
      setWasteLines([blankQtyLine()]);
      wasteForm.resetFields();
      await Promise.all([loadItems(), loadMovements(), loadValuation()]);
    } catch (e: any) {
      message.error(e?.message || "Failed to record write-off");
    } finally {
      setPosting(false);
    }
  };

  const postCount = async () => {
    const values = await countForm.validateFields();
    if (countLines.some((l) => !l.stock_item_id)) {
      message.error("Every count line needs an item");
      return;
    }
    setPosting(true);
    try {
      const res = await financeService.recordAdjustment({
        business_date: values.business_date.format("YYYY-MM-DD"),
        notes: values.notes || null,
        department_id: values.department_id || null,
        lines: countLines.map((l) => ({
          stock_item_id: l.stock_item_id,
          counted_quantity: Number(l.counted_quantity ?? 0),
        })),
      });
      message.success(
        res?.quantity === 0
          ? "Count matched the book — nothing to adjust"
          : `Count adjustment posted — ${formatLkr(res?.value || 0)}`,
      );
      setCountLines([blankQtyLine()]);
      countForm.resetFields();
      await Promise.all([loadItems(), loadMovements(), loadValuation()]);
    } catch (e: any) {
      message.error(e?.message || "Failed to post count adjustment");
    } finally {
      setPosting(false);
    }
  };

  /* ------------------------------------------------------------
   * Screens
   * ------------------------------------------------------------ */

  const itemOptions = items.map((i) => ({ value: i.id, label: `${i.code} — ${i.name}` }));

  const recipeItemOptions = Array.from(recipeItemById.values()).map((i) => ({
    value: i.id,
    label: `${i.code} — ${i.name} (${i.unit})${i.is_active ? "" : " · inactive"}`,
    disabled: !i.is_active,
  }));

  const recipeCostPerServing = recipeLines.reduce((total, line) => {
    const quantity = recipeStockQuantity(line);
    if (quantity === null) return total;
    return total + quantity * recipeUnitPrice(line);
  }, 0);

  const recipeBatchCost = recipeCostPerServing * recipeServings;

  const recipeHasMissingCost = recipeLines.some((line) => {
    const item = line.stock_item_id ? recipeItemById.get(line.stock_item_id) : undefined;
    return Boolean(item) && line.manual_unit_price === null && Number(item?.avg_cost ?? 0) <= 0;
  });

  const lowStockItems = useMemo(
    () =>
      items.filter(
        (item) =>
          item.is_active &&
          Number(item.reorder_level) > 0 &&
          Number(item.on_hand) <= Number(item.reorder_level),
      ),
    [items],
  );

  const stockTab = (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button
          size="small"
          type={includeInactive ? "default" : "primary"}
          onClick={() => setIncludeInactive(false)}
        >
          Active only
        </Button>
        <Button
          size="small"
          type={includeInactive ? "primary" : "default"}
          onClick={() => setIncludeInactive(true)}
        >
          Include inactive
        </Button>
        <Button onClick={loadItems}>Refresh</Button>
        {canEdit && (
          <Button type="primary" onClick={() => openItemModal()}>
            New stock item
          </Button>
        )}
      </Space>
      <Table
        rowKey="id"
        size="small"
        columns={itemColumns}
        dataSource={items}
        loading={loading}
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        locale={{ emptyText: <Empty description="No stock items yet" /> }}
      />
    </>
  );

  const movementsTab = (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Any item"
          style={{ width: 240 }}
          value={movItem}
          onChange={setMovItem}
          options={itemOptions}
        />
        <Select
          allowClear
          placeholder="Any movement"
          style={{ width: 180 }}
          value={movType}
          onChange={setMovType}
          options={MOVEMENT_TYPES}
        />
        <RangePicker
          value={movRange}
          onChange={(d) => setMovRange(d && d[0] && d[1] ? [d[0], d[1]] : null)}
        />
        <Text type="secondary">latest 200 rows</Text>
      </Space>
      <Table
        rowKey="id"
        size="small"
        loading={movLoading}
        dataSource={movements}
        scroll={{ x: 1150 }}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        columns={[
          {
            title: "Date",
            dataIndex: "business_date",
            key: "business_date",
            width: 115,
            render: (v: string) => dayjs(v).format("MMM D, YYYY"),
          },
          {
            title: "Type",
            dataIndex: "movement_type",
            key: "movement_type",
            width: 150,
            render: (v: string) => <Tag color={MOVEMENT_COLOR[v]}>{v}</Tag>,
          },
          {
            title: "Item",
            key: "item",
            render: (_: any, m: StockMovement) => `${m.item_code} — ${m.item_name}`,
          },
          {
            title: "Qty",
            dataIndex: "quantity",
            key: "quantity",
            width: 90,
            align: "right" as const,
            render: (v: number) => (v < 0 ? <Text type="danger">{v}</Text> : v),
          },
          {
            title: "Unit cost",
            dataIndex: "unit_cost",
            key: "unit_cost",
            width: 120,
            align: "right" as const,
            render: (v: number) => formatLkr(v),
          },
          {
            title: "Value",
            dataIndex: "line_value",
            key: "line_value",
            width: 130,
            align: "right" as const,
            render: (v: number) => formatLkr(v),
          },
          {
            title: "Source",
            dataIndex: "ref_type",
            key: "ref_type",
            width: 130,
            ellipsis: true,
          },
          {
            title: "Reverses",
            dataIndex: "reverses_movement_id",
            key: "reverses_movement_id",
            width: 100,
            render: (v: string | null) => (v ? <Tag color="warning">compensating</Tag> : null),
          },
          {
            title: "Note",
            dataIndex: "notes",
            key: "notes",
            ellipsis: true,
            render: (v: string | null) => v || "—",
          },
        ]}
        locale={{ emptyText: <Empty description="No movements yet" /> }}
      />
    </>
  );

  const recipeTab = (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        title="A menu item with no recipe posts no issue"
        description="That gap is reported as recipe coverage next to Food Cost %, never as a better margin. Saving a recipe replaces it outright — the list is the document."
      />
      <Space style={{ marginBottom: 12 }} wrap>
        <Button onClick={loadRecipes}>Refresh</Button>
        {canEdit && (
          <Button type="primary" onClick={() => openRecipe()}>
            New recipe
          </Button>
        )}
      </Space>
      <Table
        rowKey="menuItemId"
        size="small"
        dataSource={grouped}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        columns={[
          { title: "Menu item", dataIndex: "name", key: "name" },
          {
            title: "Ingredients",
            key: "lines",
            render: (_: any, g: { lines: RecipeLine[] }) => (
              <Space size={4} wrap>
                {g.lines.map((l) => (
                  <Tag key={l.id} color={l.stock_item_is_active ? undefined : "warning"}>
                    {l.stock_item_code} × {l.quantity} {l.unit}
                    {l.stock_item_is_active ? "" : " · inactive"}
                  </Tag>
                ))}
              </Space>
            ),
          },
          {
            title: "Cost / serving",
            dataIndex: "costPerServing",
            key: "costPerServing",
            width: 140,
            align: "right" as const,
            render: (v: number) => <Text strong>{formatLkr(v)}</Text>,
          },
          {
            title: "",
            key: "actions",
            width: 90,
            render: (_: any, g: { menuItemId: string; name: string; lines: RecipeLine[] }) =>
              canEdit ? (
                <Button size="small" onClick={() => openRecipe(g)}>
                  Edit
                </Button>
              ) : null,
          },
        ]}
        locale={{ emptyText: <Empty description="No recipes configured yet" /> }}
      />
    </>
  );

  const writeOffTab = (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={12}>
        <Card size="small" title="Write-off (spoilage, breakage, consumed)">
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            title="Refuses a closed business date"
            description="A person recording waste after the day is closed gets a 409 rather than a post that lands on the wrong day. Automatic recipe issues queue; this does not. Only active items with stock on hand are listed, and a write-off cannot take stock below zero."
          />
          <Form form={wasteForm} layout="vertical" initialValues={{ business_date: dayjs() }}>
            <Row gutter={12}>
              <Col span={10}>
                <Form.Item name="business_date" label="Date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: "100%" }} allowClear={false} />
                </Form.Item>
              </Col>
              <Col span={14}>
                <Form.Item name="department_id" label="Department">
                  <Select
                    allowClear
                    placeholder="Chosen from the item's category"
                    options={departments.map((d) => ({ value: d.id, label: d.name }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="reason" label="Reason" rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="Out of date, dropped, burned…" maxLength={200} />
            </Form.Item>
          </Form>
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={wasteLines}
            columns={qtyColumns("quantity")}
          />
          <Space style={{ marginTop: 12 }} wrap>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => addQtyLine(setWasteLines)}
            >
              Add line
            </Button>
            <Button type="primary" loading={posting} onClick={postWaste}>
              Record write-off
            </Button>
          </Space>
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card size="small" title="Stock count adjustment">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            title="The difference is a movement, not an edit"
            description="Counted minus book posts as one signed row against 5400, so the count and the reason it was needed both stay in the history. A count that matches posts nothing at all."
          />
          <Form form={countForm} layout="vertical" initialValues={{ business_date: dayjs() }}>
            <Row gutter={12}>
              <Col span={10}>
                <Form.Item name="business_date" label="Count date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: "100%" }} allowClear={false} />
                </Form.Item>
              </Col>
              <Col span={14}>
                <Form.Item name="department_id" label="Department">
                  <Select
                    allowClear
                    placeholder="Optional"
                    options={departments.map((d) => ({ value: d.id, label: d.name }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="notes" label="Notes">
              <Input placeholder="Who counted, where, why" maxLength={200} />
            </Form.Item>
          </Form>
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={countLines}
            columns={qtyColumns("counted_quantity", true)}
          />
          <Space style={{ marginTop: 12 }} wrap>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => addQtyLine(setCountLines)}
            >
              Add line
            </Button>
            <Button type="primary" loading={posting} onClick={postCount}>
              Post adjustment
            </Button>
          </Space>
        </Card>
      </Col>
    </Row>
  );

  const control = valuation?.control;
  const lowAtDate = valuation?.items.filter((item) => item.is_active && item.below_reorder) ?? [];
  const valuationTab = (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <DatePicker
          value={valuationDate}
          onChange={setValuationDate}
          allowClear={false}
        />
        <Button onClick={loadValuation}>Refresh</Button>
        <Text type="secondary">as at the end of the selected business date</Text>
      </Space>

      {control && (
        <Alert
          type={control.matched ? "success" : "error"}
          showIcon
          style={{ marginBottom: 12 }}
          title={
            control.matched
              ? `Shelf and ledger agree — ${formatLkr(control.stock_ledger_value)}`
              : `Variance of ${formatLkr(control.variance)} between the stock ledger and GL 1200`
          }
          description={`Stock movements total ${formatLkr(
            control.stock_ledger_value,
          )}; the inventory account holds ${formatLkr(control.gl_inventory_balance)}. ${
            control.matched
              ? "Nothing can debit 1200 outside a purchase invoice, which is what keeps these equal."
              : "Investigate before relying on any cost figure below."
          }`}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small" loading={valLoading}>
            <Statistic
              title="Stock value"
              value={valuation?.total_value ?? 0}
              precision={2}
              suffix="LKR"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" loading={valLoading}>
            <Statistic title="Items valued" value={valuation?.item_count ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" loading={valLoading}>
            <Statistic
              title="Below reorder level"
              value={lowAtDate.length}
              valueStyle={lowAtDate.length > 0 ? { color: "#faad14" } : undefined}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {lowAtDate.length
                ? lowAtDate
                    .map(
                      (item) =>
                        `${item.name}: ${Number(item.on_hand)} ${item.unit} (reorder ${Number(item.reorder_level)} ${item.unit})`,
                    )
                    .join("; ")
                : "nothing running low"}
            </Text>
          </Card>
        </Col>
      </Row>

      <Table
        rowKey="id"
        size="small"
        loading={valLoading}
        dataSource={valuation?.items ?? []}
        scroll={{ x: 1050 }}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        columns={[
          { title: "Code", dataIndex: "code", key: "code", width: 100 },
          { title: "Item", dataIndex: "name", key: "name", ellipsis: true },
          { title: "Unit", dataIndex: "unit", key: "unit", width: 70 },
          {
            title: "Category",
            dataIndex: "category_name",
            key: "category_name",
            width: 120,
            render: (v: string | null, row: StockValuationReport["items"][number]) =>
              v || row.category,
          },
          {
            title: "On hand",
            dataIndex: "on_hand",
            key: "on_hand",
            width: 120,
            align: "right" as const,
            render: (v: number, row: StockValuationReport["items"][number]) => (
              <Text type={v < 0 ? "danger" : undefined}>
                {v} {row.unit}
              </Text>
            ),
          },
          {
            title: "Reorder at",
            dataIndex: "reorder_level",
            key: "reorder_level",
            width: 120,
            align: "right" as const,
            render: (v: number, row: StockValuationReport["items"][number]) =>
              Number(v) > 0 ? `${Number(v)} ${row.unit}` : "—",
          },
          {
            title: "Unit cost",
            dataIndex: "avg_cost",
            key: "avg_cost",
            width: 130,
            align: "right" as const,
            render: (v: number) => formatLkr(v),
          },
          {
            title: "Value",
            dataIndex: "value",
            key: "value",
            width: 140,
            align: "right" as const,
            render: (v: number) => <Text strong>{formatLkr(v)}</Text>,
          },
          {
            title: "Stock state",
            key: "stock_state",
            width: 110,
            render: (_: any, row: StockValuationReport["items"][number]) =>
              !row.is_active ? (
                <Tag>Inactive</Tag>
              ) : row.below_reorder ? (
                <Tag color="warning">Low</Tag>
              ) : row.on_hand <= 0 ? (
                <Tag>Out</Tag>
              ) : (
                <Tag color="success">OK</Tag>
              ),
          },
        ]}
        locale={{ emptyText: <Empty description="Nothing on the shelf yet" /> }}
      />
    </>
  );

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="warning"
          showIcon
          title="No inventory permission"
          description="Your account can view finance but not the cupboard. Ask for inventory:view to see stock, recipes and counts."
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginTop: 0 }}>
        Inventory
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Quantities move; money follows them into 1200 Inventory and out to 5100 / 5200 / 5300 /
        5400. Every figure below carries the control that proves the shelf and the ledger are
        still the same story.
      </Text>

      {error && (
        <Alert
          type="error"
          showIcon
          title="Failed to load"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      {lowStockItems.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={`${lowStockItems.length} stock item${lowStockItems.length === 1 ? "" : "s"} at or below reorder level`}
          description={
            <>
              {lowStockItems.slice(0, 5).map((item) => (
                <div key={item.id}>
                  <Text strong>{item.name}</Text>: {Number(item.on_hand)} {item.unit} on hand
                  {" "}(reorder {Number(item.reorder_level)} {item.unit})
                </div>
              ))}
              {lowStockItems.length > 5 && (
                <Text type="secondary">and {lowStockItems.length - 5} more — see Valuation.</Text>
              )}
            </>
          }
        />
      )}

      <Tabs
        items={[
          { key: "stock", label: "Stock items", children: stockTab },
          { key: "movements", label: "Movements", children: movementsTab },
          { key: "recipes", label: "Recipes", children: recipeTab },
          { key: "writeoff", label: "Write-offs & counts", children: writeOffTab },
          { key: "valuation", label: "Valuation", children: valuationTab },
        ]}
      />

      <Modal
        open={itemModal.open}
        title={itemModal.editing ? `Edit ${itemModal.editing.code}` : "New stock item"}
        onCancel={() => setItemModal({ open: false })}
        onOk={submitItem}
        okText={itemModal.editing ? "Save" : "Create"}
        destroyOnClose
      >
        {itemUnitChanged && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            title={`Unit changes from ${itemModal.editing?.unit} to ${itemUnit}`}
            description="This item has no stock, purchase or recipe history, so the label can still be corrected. Reorder levels are not converted."
          />
        )}
        <Form
          form={itemForm}
          layout="vertical"
          key={itemModal.editing?.id || "new-item"}
          initialValues={{ reorder_level: 0, is_active: true }}
        >
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="code" label="Code" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="RICE-1" maxLength={30} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="Basmati rice" maxLength={120} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="unit"
                label="Unit"
                rules={[{ required: true, message: "Select a unit" }]}
                extra={
                  itemModal.editing?.unit_locked
                    ? "Locked: this item has stock, purchase or recipe history."
                    : "Fixed unit list. Adding a unit requires a system update."
                }
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={unitsLoading}
                  disabled={itemModal.editing?.unit_locked}
                  options={itemUnitOptions}
                  placeholder="Select a unit"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: "Select a category" }]}
                extra={
                  <Button
                    type="link"
                    size="small"
                    icon={<SettingOutlined />}
                    style={{ padding: 0 }}
                    onClick={() => setCategoryModalOpen(true)}
                  >
                    Manage categories
                  </Button>
                }
              >
                <Select
                  loading={categoryLoading}
                  options={categoryOptions}
                  placeholder="Select a category"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="reorder_level"
                label="Reorder level"
                extra="Low-stock alerts use this quantity in the same unit as the item."
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  addonAfter={itemUnit || "unit"}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="is_active"
            label="Active"
            valuePropName="checked"
            extra="Inactive items stay in history but cannot be bought, issued or written off."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <StockCategoryModal
        open={categoryModalOpen}
        categories={categories}
        onClose={() => setCategoryModalOpen(false)}
        onChanged={async (createdCode) => {
          await Promise.all([loadCategories(), loadItems()]);
          if (createdCode) itemForm.setFieldValue("category", createdCode);
        }}
      />

      <Modal
        open={recipeModal.open}
        title={
          recipeModal.menuItemName
            ? `Recipe — ${recipeModal.menuItemName}`
            : "New recipe"
        }
        onCancel={() => setRecipeModal({ open: false })}
        onOk={saveRecipe}
        okText="Save recipe"
        confirmLoading={recipeSaving}
        width={1000}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="Saving replaces the whole recipe"
          description="Remove a line here and it stops being issued. Quantities may be entered in g/kg or ml/l and are converted to the stock unit on save. Manual prices are per stock unit and affect recipe costing only — stock value and POS issues continue to use the item's weighted-average cost."
        />
        {!recipeModal.menuItemId && (
          <Form layout="vertical" style={{ marginBottom: 12 }}>
            <Form.Item label="Menu item" required>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Choose the dish this recipe belongs to"
                value={recipeModal.menuItemId}
                onChange={(v) => {
                  const found = menuItems.find((m: any) => m.id === v);
                  setRecipeModal({
                    open: true,
                    menuItemId: v,
                    menuItemName: found?.name || found?.item_name || "Menu item",
                  });
                }}
                options={menuItems.map((m: any) => ({
                  value: m.id,
                  label: m.name || m.item_name || m.id,
                }))}
              />
            </Form.Item>
          </Form>
        )}
        <Table
          rowKey="key"
          size="small"
          pagination={false}
          dataSource={recipeLines}
          scroll={{ x: 860 }}
          columns={[
            {
              title: "Stock item",
              key: "item",
              width: 230,
              onCell: topAlignCell,
              render: (_: any, l: RecipeDraftLine) => (
                <div style={recipeFieldStyle}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="Item"
                    value={l.stock_item_id}
                    options={recipeItemOptions}
                    onChange={(v) => {
                      const item = recipeItemById.get(v);
                      setRecipeLines((prev) =>
                        prev.map((x) =>
                          x.key === l.key
                            ? {
                                ...x,
                                stock_item_id: v,
                                unit: item?.unit,
                                manual_unit_price: null,
                              }
                            : x,
                        ),
                      );
                    }}
                  />
                </div>
              ),
            },
            {
              title: "Qty per serving",
              key: "qty",
              width: 240,
              onCell: topAlignCell,
              render: (_: any, l: RecipeDraftLine) => {
                const item = l.stock_item_id ? recipeItemById.get(l.stock_item_id) : undefined;
                const stockUnit = item?.unit;
                const converted = stockUnit
                  ? convertQuantity(
                      Number(l.quantity),
                      l.unit || stockUnit,
                      stockUnit,
                      units,
                    )
                  : null;
                return (
                  <div style={recipeFieldStyle}>
                    <Space.Compact style={{ width: "100%" }}>
                      <InputNumber
                        min={0.001}
                        step={0.001}
                        precision={3}
                        style={{ width: "100%" }}
                        value={l.quantity}
                        disabled={!stockUnit}
                        onChange={(v) =>
                          setRecipeLines((prev) =>
                            prev.map((x) =>
                              x.key === l.key ? { ...x, quantity: Number(v ?? 0) } : x,
                            ),
                          )
                        }
                      />
                      <Select
                        style={{ width: 82 }}
                        value={l.unit || stockUnit}
                        options={stockUnit ? unitOptions(stockUnit, units) : []}
                        disabled={!stockUnit}
                        onChange={(unit) =>
                          setRecipeLines((prev) =>
                            prev.map((x) => (x.key === l.key ? { ...x, unit } : x)),
                          )
                        }
                      />
                    </Space.Compact>
                    {stockUnit && l.unit && l.unit !== stockUnit && converted !== null && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        = {roundQuantity(converted)} {stockUnit}
                      </Text>
                    )}
                  </div>
                );
              },
            },
            {
              title: "Stock unit price",
              key: "price",
              width: 165,
              onCell: topAlignCell,
              render: (_: any, l: RecipeDraftLine) => {
                const item = l.stock_item_id ? recipeItemById.get(l.stock_item_id) : undefined;
                const autoPrice = Number(item?.avg_cost ?? 0);
                return (
                  <div style={recipeFieldStyle}>
                    <InputNumber
                      min={0}
                      step={0.01}
                      style={{ width: "100%" }}
                      placeholder={
                        !item
                          ? "Select item"
                          : autoPrice > 0
                            ? `Auto ${formatLkr(autoPrice)} / ${item.unit}`
                            : "Enter manual price"
                      }
                      value={l.manual_unit_price}
                      disabled={!item}
                      onChange={(v) =>
                        setRecipeLines((prev) =>
                          prev.map((x) =>
                            x.key === l.key
                              ? {
                                  ...x,
                                  manual_unit_price:
                                    v === null || v === undefined ? null : Number(v),
                                }
                              : x,
                          ),
                        )
                      }
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {l.manual_unit_price === null ? (
                        "Auto stock cost"
                      ) : (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: "auto", fontSize: 11 }}
                          onClick={() =>
                            setRecipeLines((prev) =>
                              prev.map((x) =>
                                x.key === l.key ? { ...x, manual_unit_price: null } : x,
                              ),
                            )
                          }
                        >
                          Manual per {item?.unit || "stock unit"} — use auto cost
                        </Button>
                      )}
                    </Text>
                  </div>
                );
              },
            },
            {
              title: "Cost / serving",
              key: "cost",
              width: 110,
              align: "right" as const,
              onCell: topAlignCell,
              render: (_: any, l: RecipeDraftLine) => {
                const quantity = recipeStockQuantity(l);
                return (
                  <div style={recipeTextStyle}>
                    {quantity === null ? "—" : formatLkr(quantity * recipeUnitPrice(l))}
                  </div>
                );
              },
            },
            {
              title: `Total qty (${recipeServings} serving${recipeServings === 1 ? "" : "s"})`,
              key: "batchQty",
              width: 125,
              align: "right" as const,
              onCell: topAlignCell,
              render: (_: any, l: RecipeDraftLine) => {
                const item = l.stock_item_id ? recipeItemById.get(l.stock_item_id) : undefined;
                const quantity = recipeStockQuantity(l);
                return (
                  <div style={recipeTextStyle}>
                    {quantity === null || !item
                      ? "—"
                      : `${roundQuantity(quantity * recipeServings)} ${item.unit}`}
                  </div>
                );
              },
            },
            {
              title: `Total cost (${recipeServings} serving${recipeServings === 1 ? "" : "s"})`,
              key: "batchCost",
              width: 125,
              align: "right" as const,
              onCell: topAlignCell,
              render: (_: any, l: RecipeDraftLine) => {
                const quantity = recipeStockQuantity(l);
                return (
                  <div style={recipeTextStyle}>
                    {quantity === null
                      ? "—"
                      : formatLkr(quantity * recipeUnitPrice(l) * recipeServings)}
                  </div>
                );
              },
            },
            {
              title: "",
              key: "actions",
              width: 50,
              onCell: topAlignCell,
              render: (_: any, l: RecipeDraftLine) => (
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={recipeLines.length === 1}
                  onClick={() =>
                    setRecipeLines((prev) => prev.filter((x) => x.key !== l.key))
                  }
                />
              ),
            },
          ]}
        />
        <Space style={{ marginTop: 12 }} wrap>
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setRecipeLines((prev) => [...prev, blankRecipeLine()])}
          >
            Add ingredient
          </Button>
          <Text strong>Servings</Text>
          <InputNumber
            min={1}
            step={1}
            precision={0}
            style={{ width: 80 }}
            value={recipeServings}
            onChange={(v) => setRecipeServings(Math.max(1, Math.floor(Number(v) || 1)))}
          />
          <Text>
            Per serving <Text strong>{formatLkr(recipeCostPerServing)}</Text>
          </Text>
          <Text>
            Total <Text strong>{formatLkr(recipeBatchCost)}</Text>
          </Text>
        </Space>
        {recipeHasMissingCost && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            title="Some ingredients have no stock cost yet"
            description="Enter a manual unit price on those lines, or receive a purchase first so the weighted-average cost is available."
          />
        )}
      </Modal>
    </div>
  );
};

export default InventoryPage;
